import { OVERDUE_STATUSES, OverdueStatus } from "../../../config/params";
/* eslint-disable max-len */
import React, { useEffect, useState } from "react";
import { getOverdueCtaText, handleOverdueCtaClick } from "../../../components/OutstandingPaymentCard/OutstandingPaymentCard";

import { Divider } from "@mui/material";
import CounterCircle from "../../../components/CounterCircle/CounterCircle";
import { IPetItemResponse } from "../../../services/pet-list/pet-list.interface";
import { ISquareInvoices } from "../../../services/delinquent-payment/delinquent-payment.interface";
import { IWellnessPlanBenefitUsage } from "../../../services/wellness-plan/wellness-plan.interface";
import { SocCoverageCard } from "../../../components";
import { StyledBookingFlowButton } from "../BookingScheduleContainer/BookingScheduleContainer.style";
import WellnessMemberPlanCard from "../../../components/WellnessMemberPlanCard/WellnessMemberPlanCard";
import { fromUnixToFullDateNumNew } from "../../../utils/unix-timestamp";
import { useModal } from "../../../context/ModalContext";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../../hooks/redux-hooks/user-store/user.store";

interface IWellnessMemberContainer {
  petList: IPetItemResponse[];
  handlePetChange: (petId: string, category?: string) => void;
  petData: IPetItemResponse;
  pendingInvoices: ISquareInvoices[];
  benifitUsageData: IWellnessPlanBenefitUsage[];
  planOverdueStatus: OverdueStatus | null;
  paymentUrl?: string;
  totalSavings: number
  planName: string;
  examsCount: number;
  hasWellnessPlan?: boolean
  isScratchpay?: boolean
}


const renderCoverageCards = (title: string, cards: JSX.Element[], isCompleted: boolean) => (
  <>
    <div className="font-semibold flex justify-between">
      <div className="self-center">{title}</div>
      <div className={`py-2 px-3 text-sm rounded-full ${isCompleted ? "bg-light-purple text-dark-purple" : "bg-turquoise-1 text-promo-turquoise"}`}>{isCompleted ? "Completed" : "Available"}</div>
    </div>
    <div className="flex gap-2">{cards}</div>
    <Divider sx={{ opacity: 0.8 }} className="w-full !m-0 border !border-gray-3" />
  </>
);

const WellnessMemberContainer = ({
  petList,
  handlePetChange,
  petData,
  paymentUrl,
  planOverdueStatus,
  pendingInvoices,
  benifitUsageData, totalSavings, planName, examsCount, hasWellnessPlan, isScratchpay }: IWellnessMemberContainer) => {
  const [wellnessBenifitData, setWellnessBenefitData] = useState<IWellnessPlanBenefitUsage[]>();
  const [vaccinationDetails, setVaccinationDetails] = useState<JSX.Element[]>([]);
  const [diagnosticDetails, setDiagnosticDetails] = useState<JSX.Element[]>([]);
  const [microchipDetails, setMicrochipDetails] = useState<JSX.Element[]>([]);
  const [bloodworkDetails, setBloodworkDetails] = useState<JSX.Element[]>([]);
  const [surgicalBenefit, setSurgicalBenefit] = useState<IWellnessPlanBenefitUsage>();
  const [completions, setCompletions] = useState<Record<string, boolean>>({});

  const { user } = useUserStore();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { openModal } = useModal();

  const isPlanViewDisabled = (overdueStatus: string) => {
    if (planOverdueStatus) {
      if ((planOverdueStatus === OVERDUE_STATUSES.PAUSED) || (planOverdueStatus === OVERDUE_STATUSES.CANCELLED)) {
        return true;
      } else {
        return false;
      }
    }
  };


  useEffect(() => {
    if (benifitUsageData) {
      setWellnessBenefitData(benifitUsageData);
    }
  }, [benifitUsageData]);


  useEffect(() => {
    if (wellnessBenifitData && wellnessBenifitData.length > 0) {
      // sets completed tag
      const completeions = {
        "Vaccination": true,
        "Diagnostic": true,
        "Preventive Care": true,
        "Procedure": true,
        "Professional Services": true,
        "Consultation": true,
        "Exams": true,
        "Microchip": true,
        "Bloodwork": true,
      };
      const vaccinationResult: JSX.Element[] = wellnessBenifitData
        .filter((item) => item.mappedCategory === "Vaccination")
        .map((item, index) => {
          const lastuseddate = fromUnixToFullDateNumNew(item.modified_at,
            user?.location?.timeZone);

          if (item.used !== item.allocated) {
            completeions["Vaccination"] = false;
          }

          return (<SocCoverageCard
            key={`vaccination-${index}`}
            title={item.displayName}
            counterCurrent={item.used}
            counterMax={item.allocated}
            lastGivenDate={`${lastuseddate.day}/${lastuseddate.month}/${lastuseddate.year}`}
            type={item.mappedCategory}
          />);
        });

      const diagnosticResult: JSX.Element[] = wellnessBenifitData
        .filter((item) => item.mappedCategory === "Diagnostic")
        .map((item, index) => {
          const lastuseddate = fromUnixToFullDateNumNew(item.modified_at,
            user?.location?.timeZone);

          if (item.used !== item.allocated) {
            completeions["Diagnostic"] = false;
          }

          return <SocCoverageCard
            key={`diagnostic-${index}`}
            title={item.displayName}
            counterCurrent={item.used}
            counterMax={item.allocated}
            lastGivenDate={`${lastuseddate.day}/${lastuseddate.month}/${lastuseddate.year}`}
            type={item.mappedCategory}
          />;
        });

      const microchipResult: JSX.Element[] = wellnessBenifitData
        .filter((item) => item.mappedCategory === "Microchip")
        .map((item, index) => {
          const lastuseddate = fromUnixToFullDateNumNew(item.modified_at,
            user?.location?.timeZone);

          if (item.used !== item.allocated) {
            completeions["Microchip"] = false;
          }

          return <SocCoverageCard
            key={`microchip-${index}`}
            title={item.displayName}
            counterCurrent={item.used}
            counterMax={item.allocated}
            lastGivenDate={`${lastuseddate.day}/${lastuseddate.month}/${lastuseddate.year}`}
            type={item.mappedCategory}
          />;
        });

      const bloodworkResult: JSX.Element[] = wellnessBenifitData
        .filter((item) => item.mappedCategory === "Bloodwork")
        .map((item, index) => {
          const lastuseddate = fromUnixToFullDateNumNew(item.modified_at,
            user?.location?.timeZone);

          if (item.used !== item.allocated) {
            completeions["Bloodwork"] = false;
          }

          return <SocCoverageCard
            key={`bloodwork-${index}`}
            title={item.displayName}
            counterCurrent={item.used}
            counterMax={item.allocated}
            lastGivenDate={`${lastuseddate.day}/${lastuseddate.month}/${lastuseddate.year}`}
            type={item.mappedCategory}
          />;
        });

      const surgicalResult = wellnessBenifitData.find(
        (item) => item.mappedCategory === "Procedure",
      );

      setCompletions(completeions);
      setMicrochipDetails(microchipResult);
      setVaccinationDetails(vaccinationResult);
      setDiagnosticDetails(diagnosticResult);
      setBloodworkDetails(bloodworkResult);
      setSurgicalBenefit(surgicalResult);
    }
  }, [wellnessBenifitData]);

  return (
    <div className="w-full bg-taupe-15 flex flex-col items-center p-4 gap-4">
      <div
        className={`w-full ${
          planOverdueStatus && !isLoading ? "bg-white rounded-xl" : ""
        }`}
      >
        <WellnessMemberPlanCard
          petList={petList}
          petData={petData}
          handlePetChange={handlePetChange}
          isSubscriptionBenefitAvailable
          planOverdueStatus={planOverdueStatus}
          setIsLoading={setIsLoading}
          totalSavings={totalSavings}
          planName={planName}
          examsCount={examsCount}
          hasWellnessPlan={hasWellnessPlan}
          isScratchpay={isScratchpay}
        />
        {planOverdueStatus && !isLoading && (
          <div className="p-4">
            <StyledBookingFlowButton
              disabled={false}
              fullWidth
              color="primary"
              size="small"
              onClick={() =>
                handleOverdueCtaClick(
                  planOverdueStatus,
                  petData.name,
                  "totalcare_plan",
                  pendingInvoices,
                  Boolean(isScratchpay),
                  paymentUrl,
                  navigate,
                  openModal,
                  petData.id,
                  planName,
                )
              }
            >
              {getOverdueCtaText(planOverdueStatus)}
            </StyledBookingFlowButton>
          </div>
        )}
      </div>

      <div
        className={`w-full bg-gray-2 px-4 py-6 flex flex-col gap-4 rounded-xl
        ${
    planOverdueStatus && isPlanViewDisabled(planOverdueStatus) ?
      "opacity-30" :
      ""
    }`}
      >
        {surgicalBenefit ? (
          <div className="flex flex-col gap-3 w-full">
            <div className="font-semibold text-light-black">Surgical Benefit Details</div>
            <Divider sx={{ opacity: 0.8 }} className="w-full !m-0 border !border-gray-3" />
            <div className="bg-white border border-taupe-15 flex flex-col gap-2 items-center p-3 rounded-lg w-full">
              <CounterCircle currentValue={surgicalBenefit.used} maxValue={surgicalBenefit.allocated} />
              <div className="flex flex-col gap-0.5 items-center text-center">
                <div className="text-sm font-semibold text-light-black">
                  {surgicalBenefit.displayName}
                </div>
                <div className="text-xs text-gray-1">
                  Your plan includes 1 surgery procedure per year
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {microchipDetails.length ?
          renderCoverageCards(
            "Microchip",
            [
              <div key={"microchip"} className="w-full">
                <div className="w-full grid grid-cols-2 md:grid-cols-3 gap-2">
                  {microchipDetails}
                </div>
              </div>,
            ],
            completions["Microchip"],
          ) :
          null}

        {vaccinationDetails.length ?
          renderCoverageCards(
            "Vaccines",
            [
              <div key={"vaccinations"} className="w-full">
                <div className="w-full grid grid-cols-2 md:grid-cols-3 gap-2">
                  {vaccinationDetails}
                </div>
              </div>,
            ],
            completions["Vaccination"],
          ) :
          null}

        {diagnosticDetails.length ?
          renderCoverageCards(
            "Diagnostics",
            [
              <div key={"diagnostics"} className="w-full">
                <div className="w-full grid grid-cols-2 md:grid-cols-3 gap-2">
                  {diagnosticDetails}
                </div>
              </div>,
            ],
            completions["Diagnostic"],
          ) :
          null}

        {bloodworkDetails.length ?
          renderCoverageCards(
            "Bloodwork",
            [
              <div key={"bloodwork"} className="w-full">
                <div className="w-full grid grid-cols-2 md:grid-cols-3 gap-2">
                  {bloodworkDetails}
                </div>
              </div>,
            ],
            completions["Bloodwork"],
          ) :
          null}

        <div className="flex justify-center text-sm">
          <p className="text-center w-4/5">
            {planName.toLowerCase() === "base" ?
              `${planName} includes 10% off X-rays, Ultrasound, & Surgeries, and 10% off Additional Services.` :
              `${planName} includes 20% off X-rays, Ultrasound, & Surgeries, and 10% off Additional Services.`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WellnessMemberContainer;
